import { useEffect, useState } from 'react';

// Types
import { SavedCardOptionProps } from './SavedCardOption.types';

// Config
import {
  ANALYTICS_SAVED_CARD_DETAILS_TOGGLED,
  SUBMIT_ORDER_BUTTON_CLICKED,
  SUBMIT_ORDER_BUTTON_NOT_CLICKED,
} from '@constants/actionConstants';
import paymentTypeConstants from '@constants/paymentTypeConstants';
import { scrollToElement } from '@utils/index';
import { SAVED_CARD_DETAILS_FORM_ID, canSkipCVV } from '@utils/form/configs/savedCardDetails';
import { getLast4Digits } from '@utils/helpers/cardDetails';
import { cardTypeText } from '@utils/helpers/cardLogos';

// Components
import EditSavedPaymentDetails from '@components/add-payment-details/edit-saved-payment-details';
import PaymentOption from '@components/add-payment-details/payment-option';
import Container from '@components/container';
import OrderTotals from '@components/order-totals';
import PaymentButton from '@components/payment-button';
import ThreeDSecureWarning from '@components/three-d-secure-warning';

const SavedCardOption = ({
  card = {},
  confirmedDeliveryAddress,
  'data-testid': dataTest = '',
  desktopResolution = false,
  destroyForm = () => {},
  disabled = false,
  formSubmitFailed = false,
  isCardFormValid = true,
  selectedDeliveryChoiceId,
  selectedBillingAddress = {},
  resetAfterError,
  savedPaymentCards = [],
  selectedPaymentType,
  selectedSavedCard,
  setSelectedPaymentType = () => {},
  setSelectedSavedCard = () => {},
  showOrderTotals = false,
  submitPayment = () => {},
  triggerAnalyticsEvent = () => {},
}: SavedCardOptionProps) => {
  const hideCvv = canSkipCVV({
    cardType: card.type,
    selectedDeliveryChoiceId,
    selectedBillingAddress,
    confirmedDeliveryAddress,
  });

  const [canViewDetails, setCanViewDetails] = useState(hideCvv);
  const [submitButtonClickEvent, setSubmitButtonClickEvent] = useState(SUBMIT_ORDER_BUTTON_NOT_CLICKED);

  const savedCardId = `${paymentTypeConstants.SAVED_PAYMENT_CARD}:${card.id}`;
  const selected = selectedPaymentType === savedCardId;

  const setSavedCardExpandedVisibility = (visibility: boolean) => {
    setCanViewDetails(visibility);

    const analyticsVisibility = visibility ? 'Hide saved card details' : 'View saved card details';

    triggerAnalyticsEvent(ANALYTICS_SAVED_CARD_DETAILS_TOGGLED, { subType: analyticsVisibility });
  };

  const setSubmitButtonClickedStatus = () => {
    setSubmitButtonClickEvent(SUBMIT_ORDER_BUTTON_NOT_CLICKED);
  };

  const handleViewDetailsClick = (viewDetailsClicked: boolean) => {
    const toggleState = typeof viewDetailsClicked !== 'undefined' ? viewDetailsClicked : !canViewDetails;
    setCanViewDetails(toggleState);
    setSubmitButtonClickedStatus();
  };

  const handleSavedCardSelectionClick = () => {
    if (!selected) {
      destroyForm(SAVED_CARD_DETAILS_FORM_ID);
    }

    setSelectedPaymentType(savedCardId, savedPaymentCards);

    if (card.id) {
      scrollToElement({ id: card.id });
      setSelectedSavedCard(card.id);
    }
  };

  const handleSubmitClick = () => {
    setSubmitButtonClickEvent(SUBMIT_ORDER_BUTTON_CLICKED);
    submitPayment();
  };

  useEffect(() => {
    if (!isCardFormValid) {
      handleViewDetailsClick(false);
    }
  }, [isCardFormValid]);

  useEffect(() => {
    if (formSubmitFailed && selectedPaymentType === `${paymentTypeConstants.SAVED_PAYMENT_CARD}:${selectedSavedCard}`) {
      let hidden = canViewDetails;

      // View details button is in toggle state
      // To open the card details element when card input form is invalid after clicking the submit button
      // @hidden value is set to FALSE
      if (submitButtonClickEvent === SUBMIT_ORDER_BUTTON_CLICKED) {
        hidden = false;
      }

      setSavedCardExpandedVisibility(hidden);
    }
  }, [formSubmitFailed, submitButtonClickEvent, selectedPaymentType, selectedSavedCard, canViewDetails]);

  const paymentOptionLabel = `${cardTypeText(card.type || '')} ending in ${getLast4Digits(card.obfuscatedPan || '')}`;

  return (
    <PaymentOption
      key={card.id}
      cardType={card.type}
      data-testid={dataTest}
      desktopResolution={desktopResolution}
      detailsToggleOnClick={setSavedCardExpandedVisibility}
      disabled={disabled}
      errors={formSubmitFailed}
      handleViewDetailsClick={handleViewDetailsClick}
      hideCvv={hideCvv}
      id={card.id}
      isViewDetailsVisible={canViewDetails}
      label={paymentOptionLabel}
      name="paymentOption"
      onClick={handleSavedCardSelectionClick}
      selected={selected}
      showCardTypeIcon
      showHideDetailsButtonOnSelection={!hideCvv || undefined}
    >
      <Container borderTop="black" padding="0" testId={`expanded-saved-card-details-${card.id}`}>
        <Container marginBottom="1" paddingTop="2" paddingX="2">
          <ThreeDSecureWarning />
        </Container>
        <Container
          borderBottom="black"
          hidden={canViewDetails}
          id={`PaymentDetails-${card.id}`}
          padding="2"
          testId={`CardPaymentDetails-${card.id}`}
        >
          <EditSavedPaymentDetails
            hideCvv={hideCvv}
            numberOfSavedCards={savedPaymentCards.length}
            paymentDetails={card}
            resetAfterError={resetAfterError}
          />
        </Container>
        <Container padding="2">
          {showOrderTotals && <OrderTotals containerWide />}

          <PaymentButton disabled={disabled} ignoreAvailablePaymentProviders onClickOverride={handleSubmitClick} />
        </Container>
      </Container>
    </PaymentOption>
  );
};

export default SavedCardOption;
